import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Percent } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fetchFinancialPolicy, updateFinancialPolicy, type FinancialPolicy } from '@/lib/financialPolicyApi';
import { getApiErrorMessage } from '@/lib/api';

export default function FinancialPolicyPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['financial-policy'], queryFn: fetchFinancialPolicy });
  const [form, setForm] = useState<FinancialPolicy | null>(null);

  if (q.data && form === null) setForm(q.data);

  const mut = useMutation({
    mutationFn: (input: Partial<FinancialPolicy>) => updateFinancialPolicy(input),
    onSuccess: (d) => { toast.success('Financial policy saved'); setForm(d); qc.invalidateQueries({ queryKey: ['financial-policy'] }); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save')),
  });

  const set = <K extends keyof FinancialPolicy>(k: K, v: FinancialPolicy[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const num = (v: string): number => (v.trim() === '' ? 0 : Number(v));

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Financial Policy"
        subtitle="Chambers-wide financial defaults. These feed the fee/billing engine — they don't recompute money."
      />

      {q.isLoading || !form ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        // Full width with the three numeric settings across the page, matching Diary Policy.
        // A 36rem card left most of the screen empty, and its fields — each a short number in
        // a fixed 8rem box — were stacked down the left with the spacing of a long form.
        <div className="space-y-3">
          <Card>
            <CardContent className="grid gap-x-5 gap-y-3.5 p-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Default VAT rate</span>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={100} step="0.5" value={form.vatRate} onChange={(e) => set('vatRate', num(e.target.value))} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  <Percent className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Applied to new fee records for VAT-registered barristers (UK standard is 20%).</p>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Chambers commission</span>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={100} step="0.5" value={form.commissionPct} onChange={(e) => set('commissionPct', num(e.target.value))} className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  <Percent className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">The chambers' contribution taken from barristers' fees.</p>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Write-off approval threshold (£, optional)</span>
                <input type="number" min={0} value={form.writeOffApprovalThreshold ?? ''} onChange={(e) => set('writeOffApprovalThreshold', e.target.value.trim() === '' ? null : num(e.target.value))} placeholder="e.g. 500" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
                <p className="mt-1 text-xs text-muted-foreground">Write-offs above this amount require sign-off.</p>
              </label>

              {/* Last, on a row of its own: the three settings above are numbers you type, this
                  one is a switch. It sat third only because it was written third. */}
              <label className="flex items-start gap-2 sm:col-span-2 lg:col-span-3">
                <input type="checkbox" checked={form.billingApprovalRequired} onChange={(e) => set('billingApprovalRequired', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-400" />
                <span>
                  <span className="text-sm text-foreground">Require approval before a fee note is issued</span>
                  <span className="block text-xs text-muted-foreground">Adds a review step to the billing workflow.</span>
                </span>
              </label>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button variant="primary" size="md" onClick={() => mut.mutate(form)} disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Save policy'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
